import * as CL from '@design-system/component-library';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteLoaderData } from 'react-router';
import { EcommerceEventTypeV4 } from '../../common/analytics';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  cancelMsg,
  contractPriceCapitalizedMsg,
  elisaDevicesServiceMsg,
  monthsMsg,
  oneTimePaymentMsg,
  removeMsg,
  t,
} from '../../common/i18n/index.js';
import { isSquareTradeAddOn } from '../../common/utils/addOnUtils.js';
import { getPublishedCatalogs, getSelectedCatalog } from '../../common/utils/catalogUtils.js';
import { isEppDevicePriceSubType } from '../../common/utils/checkoutUtils.js';
import { userHasEmptyOrSMEPriceGroup } from '../../common/utils/employeeUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { formatSum, formatSumToString, isContractPrice } from '../../common/utils/priceUtils.js';
import { DEFAULT_MAX_ALLOWED_ITEM_QUANTITY_FOR_ORDER } from '../../common/utils/validationUtils.js';
import { AddOnPurpose } from '../../generated/api/models.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { updateCartItem } from '../../selfservice/actions/index.js';
import {
  getOffer,
  isEnrollmentProgramAliasApplicable,
  isEnrollmentProgramCompatible,
} from '../DeviceCheckout/deviceCheckoutUtils.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { handleCheckoutAnalytics } from '../EoeCheckout/checkoutAnalyticsUtils';
import { Picture } from '../Picture/Picture.js';
import { ProductDetailsAvailability } from '../ProductDetails/partials/ProductDetailsAvailability.js';
import {
  cartItemIndex,
  enrollmentProgramAliasKey,
  enrollmentProgramConsentKey,
  getCartItemUniqueKey,
} from './deviceCheckoutCartProductUtil.js';
import type { CheckoutRootLoaderData } from '../../common/loaders.js';
import type { Price, ShoppingCartAddOn, ShoppingCartItemForCheckout } from '../../common/types/checkout.js';
import type { DeviceChangeOrRedeemRequest } from '../../common/types/device.js';
import type { Catalog, CommercialProduct } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { ActionsHistory, State } from '../../selfservice/common/store.js';
import type { ContactOrPurposeOfUseCallables } from '../ContactOrPurposeOfUse/ContactOrPurposeOfUse.js';
import type { AliasSupplier } from '../EoeCheckout/steps/CartProductsStep.js';

import './DeviceCheckoutCartProduct.scss';

export type DeviceChangeSelectedContactIndex = {
  cartItemUUID: string;
  productItemIndex: number;
};

export type DeviceCheckoutCartProductProps = {
  cartItem: ShoppingCartItemForCheckout;
  getAdditionalParametersFns: (
    prepareSaveValuesFns: Record<string, AliasSupplier> | undefined,
    cartProductUnMounted?: boolean
  ) => void;
  setGetContactOrPurposeOfUseFn: (cartItemIndex?: number, fn?: ContactOrPurposeOfUseCallables['getValues']) => void;
  getCartItemDomRefFns: (
    CartItemDomRefs: () => { [parentKey: string]: { [elementKey: string]: HTMLElement } },
    cartProductUnMounted?: boolean
  ) => void;
  readOnlyMode?: boolean;
  shoppingCartMode?: boolean;
  deviceChangeSelectedContactIndex?: DeviceChangeSelectedContactIndex;
  onUpdateDeviceChangeSelection: (
    value?: DeviceChangeOrRedeemRequest,
    selectedIndex?: DeviceChangeSelectedContactIndex
  ) => void;
  isCartItemBackordered?: boolean;
  user?: AuthenticatedUser & ActionsHistory;
};

const TotalPrice = ({ price, quantity }: { price: Price; quantity: number }) => {
  const billingPeriod = price.periodic?.billingPeriod;
  const billingPeriodPrice =
    price.periodic?.price &&
    `${formatSumToString(price.periodic?.price * quantity, true)} €/${billingPeriod} ${t.XXVX(monthsMsg)}`;
  const periodicPrice = price.periodic?.price
    ? t.YO7F('{}/Month', formatSumToString(price.periodic.price * quantity))
    : '';

  return (
    <>
      {price.periodic && (
        <div>
          <div className={`of-device-checkout-cart-product__details--price ${dsClass.H4}`}>
            {billingPeriod ? billingPeriodPrice : periodicPrice}
          </div>
        </div>
      )}
      {price.onetime && (
        <div className={`of-device-checkout-cart-product__details--price ${dsClass.H4}`}>
          {price.onetime.price ? formatSumToString(price.onetime.price * quantity) : ''}
        </div>
      )}
    </>
  );
};

const PaymentType = ({ price }: { price: Price }) => (
  <div className="of-device-checkout-cart-product__details--payment-details left-column">
    {price.onetime?.price ? t.ASEI(oneTimePaymentMsg) : ''}
    {price.periodic?.payments
      ? isEppDevicePriceSubType(price)
        ? t.BCWX(`${elisaDevicesServiceMsg}, {} month contract`, price.periodic.payments.toString())
        : t.RA9V('Term of payment {} Month', price.periodic.payments.toString())
      : ''}
  </div>
);

const TotalFixedTermPrice = ({ price, quantity }: { price: Price; quantity: number }) => (
  <div className="of-device-checkout-cart-product__details--disclaimer right-column">
    {price.periodic?.payments && price.periodic.price
      ? t.W1RX('Total {}', formatSumToString(price.periodic.payments * price.periodic.price * quantity))
      : ''}
  </div>
);

export const DeviceCheckoutCartProduct = ({ ...props }: DeviceCheckoutCartProductProps) => {
  const {
    cartItem,
    getAdditionalParametersFns,
    setGetContactOrPurposeOfUseFn,
    getCartItemDomRefFns,
    readOnlyMode,
    shoppingCartMode,
    deviceChangeSelectedContactIndex,
    onUpdateDeviceChangeSelection,
    isCartItemBackordered,
    user,
  } = props;
  const dispatch = useDispatch();
  const { isLastCartItem, selectedCatalogCode, virtualCatalogItems } = useSelector(
    (state: State) => ({
      isLastCartItem: state.deviceCheckout?.cartItems.length === 1,
      selectedCatalogCode: state.selfservice?.virtualCatalogs?.selectedCatalogCode,
      virtualCatalogItems: state.selfservice?.virtualCatalogs?.items,
    }),
    deepEqual
  );
  const companyInfo = useSessionData().companyInfo;

  const { models: onlineModels } = useRouteLoaderData('checkoutRootEmployee') as CheckoutRootLoaderData;
  const offer = getOffer(cartItem.offerCode, onlineModels);

  const catalogs: Catalog[] = getPublishedCatalogs(virtualCatalogItems) || [];
  const catalog = getSelectedCatalog(catalogs, selectedCatalogCode);

  const { selectedAddOns, imageListingUrl, price, productName, quantity } = cartItem;
  const [prepareSaveValuesFns, setPrepareSaveValuesFns] = useState<Record<string, AliasSupplier>>();
  const [domRefs] = useState({});
  const [selectedContacts, setSelectedContacts] = useState<Record<string, string>>({});
  const [isLastItemModalVisible, setIsLastItemModalVisible] = useState(false);
  const [enrollmentProgramCompatible, setEnrollmentProgramCompatible] = useState(false);
  const [enrollmentProgramAliasCompatible, setEnrollmentProgramAliasApplicable] = useState(false);

  const additionalParameterSaveValues = (
    newPrepareSaveValuesFns: AliasSupplier,
    quantityIndex: number,
    parameterKey: string
  ) =>
    setPrepareSaveValuesFns((prevPrepareSaveValuesFns: Record<string, AliasSupplier>) => {
      return {
        ...prevPrepareSaveValuesFns,
        [`${parameterKey}[${quantityIndex.toString()}]`]: newPrepareSaveValuesFns,
      };
    });

  useEffect(() => {
    getAdditionalParametersFns(prepareSaveValuesFns);
  }, [prepareSaveValuesFns]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getCartItemDomRefFns(() => domRefs);
  }, [domRefs]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      getAdditionalParametersFns(prepareSaveValuesFns, true);
      getCartItemDomRefFns(() => domRefs, true);
      setGetContactOrPurposeOfUseFn();
    };
  }, []); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyInfo?.enrollmentPrograms && cartItem.offerCode && onlineModels) {
      if (catalog && catalog.enrollmentProgramConsent) {
        setEnrollmentProgramCompatible(
          isEnrollmentProgramCompatible(companyInfo.enrollmentPrograms, cartItem.offerCode, onlineModels)
        );
      }
    }
  }, [companyInfo, cartItem, onlineModels, catalog]);

  useEffect(() => {
    if (cartItem.offerCode && onlineModels) {
      if (companyInfo?.enrollmentPrograms) {
        setEnrollmentProgramAliasApplicable(isEnrollmentProgramAliasApplicable(cartItem.offerCode, onlineModels));
      } else {
        setEnrollmentProgramAliasApplicable(isEnrollmentProgramAliasApplicable(cartItem.offerCode, onlineModels));
      }
    }
  }, [companyInfo, cartItem, onlineModels]);

  const setEnrollmentProgramValueByIndex = (currentIndex: number, value: boolean) => {
    additionalParameterSaveValues(
      () => ({
        obj: value,
      }),
      currentIndex,
      enrollmentProgramConsentKey
    );
  };

  const changeCartAmount = (item: ShoppingCartItemForCheckout, newQuantity: number) => {
    if (prepareSaveValuesFns && Object.keys(prepareSaveValuesFns).length >= newQuantity) {
      const newPrepareSaveValuesFns = Object.keys(prepareSaveValuesFns).reduce((prev, current) => {
        let value = { ...prev };
        const valueIndex = Number(current.substring(current.indexOf('[') + 1, current.indexOf(']')));
        if (valueIndex < newQuantity) {
          value = { ...value, [current]: prepareSaveValuesFns[current] };
        }
        return value;
      }, {});
      if (newQuantity > 0) {
        // If quantity is updated to 0, then this component is going to be unmounted anyway
        setPrepareSaveValuesFns(newPrepareSaveValuesFns);
      }
    }

    // Send analytics event for add/remove item
    newQuantity > item.quantity
      ? handleCheckoutAnalytics([item], EcommerceEventTypeV4.ADD_TO_CART, onlineModels || [])
      : handleCheckoutAnalytics([item], EcommerceEventTypeV4.REMOVE_FROM_CART, onlineModels || []);

    // Clear getContactOrPurposeOfUseFns and selectedContacts for the removed quantity
    if (newQuantity < item.quantity) {
      for (let i = newQuantity; i < item.quantity; i++) {
        setGetContactOrPurposeOfUseFn(i);
        const removedIndex = cartItemIndex(cartItem.id, i).toString();
        const remainingSelectedContacts = Object.fromEntries(
          Object.entries(selectedContacts).filter(([key]) => key !== removedIndex)
        );
        setSelectedContacts(remainingSelectedContacts);
      }
    }

    // remove the device change selection, if it falls between newQuantity and old quantity
    if (deviceChangeSelectedContactIndex?.cartItemUUID === cartItem.id) {
      if (newQuantity < item.quantity && onUpdateDeviceChangeSelection) {
        if (deviceChangeSelectedContactIndex.productItemIndex > newQuantity - 1) {
          onUpdateDeviceChangeSelection(undefined, undefined);
        }
      }
    }

    dispatch(updateCartItem(item, newQuantity));

    // set default enrollmentProgram to the new item added to cart.
    if (newQuantity > item.quantity && enrollmentProgramCompatible) {
      setEnrollmentProgramValueByIndex(newQuantity - 1, true);
    }
  };

  const removeLastCartItem = () => {
    setIsLastItemModalVisible(false);
    changeCartAmount(cartItem, 0);
  };

  const handleAmountChange = (item: ShoppingCartItemForCheckout, newQuantity: number) => {
    if (isLastCartItem && newQuantity === 0) {
      setIsLastItemModalVisible(true);
    } else {
      changeCartAmount(item, newQuantity);
    }
  };

  React.useEffect(() => {
    // Once onload of component set the defaults for enrollment program
    if (enrollmentProgramCompatible) {
      Array.from({ length: quantity }, (_, currentIndex) => {
        setEnrollmentProgramValueByIndex(currentIndex, true);
      });
    }
  }, [enrollmentProgramCompatible]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (enrollmentProgramAliasCompatible && catalog?.enrollmentProgramAlias) {
      Array.from({ length: quantity }, (_, currentIndex) => {
        setPrepareSaveValuesFns((prevPrepareSaveValuesFns: Record<string, AliasSupplier>) => {
          return {
            ...prevPrepareSaveValuesFns,
            [`${enrollmentProgramAliasKey}[${currentIndex.toString()}]`]: () => ({
              obj: catalog.enrollmentProgramAlias ?? '',
            }),
          };
        });
      });
    }
  }, [catalog, enrollmentProgramAliasCompatible, quantity, setPrepareSaveValuesFns]);

  const addOnsToShow = selectedAddOns.filter(addOn => addOn.display);

  const shouldShowSquareTradeError = (addOn: ShoppingCartAddOn) => {
    return !userHasEmptyOrSMEPriceGroup(user?.segmentPricingGroup) && isSquareTradeAddOn(addOn);
  };

  const shouldShowRemoveAddOnButton = (addOn: ShoppingCartAddOn) =>
    !readOnlyMode && (addOn.addOnPurpose !== AddOnPurpose.BY_DEFAULT || shouldShowSquareTradeError(addOn));

  const findCommercialProduct = (cartItemPriceGuid: string): CommercialProduct | undefined =>
    offer?.commercialProducts?.find(cp => cp.commercialProductCode === cartItemPriceGuid);

  const contractPriceBadge = (cartItemPriceGuid: string) =>
    isContractPrice(findCommercialProduct(cartItemPriceGuid)?.price) ? (
      <CL.Badge color="discount" subtle text={t.JPKP(contractPriceCapitalizedMsg)} />
    ) : null;

  const priceBadge = !cartItem.deviceVoucher ? contractPriceBadge(cartItem.price.guid) : null;

  // TODO refactor this to use DeviceCheckoutListingItem
  return (
    <div
      id={getCartItemUniqueKey(cartItem)}
      className={`of-device-checkout-cart-product of-device-checkout-cart-product-${cartItem.id} ${
        readOnlyMode ? 'readonly' : 'writable'
      }`}
    >
      <CL.Grid>
        <CL.GridRow className={dsClass.NO_GUTTER}>
          <CL.GridCol colWidthXS={1} colWidthM={1} className="of-checkout__images">
            <Picture
              src={imageListingUrl}
              alt={productName}
              offerWidthAlternatives={[94]}
              renderedImageSize={{ onPhone: '100vw' }}
            />
          </CL.GridCol>
          <CL.GridCol colWidthXS={3} colWidthM={5} className="of-device-checkout-cart-product__details">
            <CL.Grid className="grid-push-1">
              <CL.GridRow>
                <CL.GridCol colWidthM={3}>
                  <h3
                    className={`${shoppingCartMode ? dsClass.H3 : dsClass.H4} ${dsClass.MARGIN_VERTICAL_0} ${dsClass.PADDING_BOTTOM_2}`}
                  >
                    {productName}
                  </h3>
                  {offer && <ProductDetailsAvailability offer={offer} />}
                </CL.GridCol>
                <CL.GridCol colWidthM={3}>
                  <CL.Grid>
                    <CL.GridRow>
                      <CL.GridCol colWidthM={6}>
                        {readOnlyMode ? (
                          <div className={`of-device-checkout-cart-product__details--quantity ${dsClass.TEXT_L}`}>
                            {t.BDBD('{} pcs', quantity.toString())}
                          </div>
                        ) : (
                          <CL.Quantity
                            currentValue={quantity}
                            onChange={newQuantity => {
                              if (cartItem.quantity !== newQuantity) {
                                handleAmountChange(cartItem, newQuantity);
                              }
                            }}
                            minValue={1}
                            maxValue={DEFAULT_MAX_ALLOWED_ITEM_QUANTITY_FOR_ORDER}
                            onRemove={() => handleAmountChange(cartItem, 0)}
                            removeLessThanMin
                          />
                        )}
                      </CL.GridCol>
                    </CL.GridRow>
                    <CL.GridRow>
                      <CL.GridCol colWidthM={6} className={dsClass.MARGIN_TOP_4}>
                        <TotalPrice price={price} quantity={quantity} />
                      </CL.GridCol>
                    </CL.GridRow>
                    {priceBadge && (
                      <CL.GridRow>
                        <CL.GridCol colWidthM={6} className={`${dsClass.MARGIN_BOTTOM_4} ${dsClass.TEXT_ALIGN_RIGHT}`}>
                          {priceBadge}
                        </CL.GridCol>
                      </CL.GridRow>
                    )}
                    {cartItem.deviceVoucher && (
                      <CL.GridRow>
                        <CL.GridCol colWidthM={6} className={`${dsClass.MARGIN_BOTTOM_4} ${dsClass.TEXT_ALIGN_RIGHT}`}>
                          <CL.Badge text={cartItem.deviceVoucher.voucherCode} className="voucher_badge" />
                        </CL.GridCol>
                      </CL.GridRow>
                    )}
                  </CL.Grid>
                </CL.GridCol>
              </CL.GridRow>
              <CL.GridRow>
                <CL.GridCol colWidthM={6} className={isCartItemBackordered ? '' : 'bottom-row'}>
                  <PaymentType price={price} />
                  {!readOnlyMode && <TotalFixedTermPrice price={price} quantity={quantity} />}
                </CL.GridCol>
              </CL.GridRow>
              {readOnlyMode && isCartItemBackordered && (
                <CL.GridRow>
                  <CL.GridCol colWidthM={6} className="bottom-row">
                    <p>{t.JVNS('Backordered')}</p>
                  </CL.GridCol>
                </CL.GridRow>
              )}
            </CL.Grid>
          </CL.GridCol>
        </CL.GridRow>
        {addOnsToShow.length > 0 && (
          <CL.GridRow className={`${dsClass.NO_GUTTER} ${dsClass.JUSTIFY_CONTENT_FLEX_END}`}>
            <CL.GridCol colWidthXS={3} colWidthM={5}>
              <CL.Grid className="of-device-checkout-cart-product__addOn-details grid-push-1">
                {addOnsToShow.map((addOn, i) => (
                  <CL.GridRow key={`addon${i}`}>
                    {shouldShowSquareTradeError(addOn) && (
                      <div className={`${dsClass.COLOR_RED_600} ${dsClass.TEXT_S}`}>
                        {t.Z85X('SquareTrade is not available and it needs to be removed from the basket.')}
                      </div>
                    )}
                    <CL.GridCol colWidthXS={4} colWidthM={3}>
                      <h4 className={dsClass.MARGIN_VERTICAL_0}>{addOn.addOnProductName}</h4>
                    </CL.GridCol>
                    <CL.GridCol colWidthXS={4} colWidthM={3}>
                      <CL.Grid className="of-device-checkout-cart-product__addOn-details--price">
                        <CL.GridRow>
                          <CL.GridCol colWidthM={6}>
                            <div className={dsClass.H4}>
                              {addOn.addOnOneTimeCharge && <div>{formatSum(addOn.addOnOneTimeCharge * quantity)}</div>}
                              {addOn.addOnMonthlyRecurringCharge && (
                                <div>
                                  {t.YO7F('{}/Month', formatSum(addOn.addOnMonthlyRecurringCharge * quantity) || '')}
                                </div>
                              )}
                            </div>
                          </CL.GridCol>
                        </CL.GridRow>
                        <CL.GridRow>
                          <CL.GridCol colWidthM={6}>
                            {shouldShowRemoveAddOnButton(addOn) && (
                              <div className="of-device-checkout-cart-product__addOn-details--remove">
                                <CL.Link
                                  className="of-link"
                                  onClick={() => {
                                    dispatch(updateCartItem(cartItem, cartItem.quantity, [addOn.addOnCode]));
                                  }}
                                >
                                  {t.R3VE(removeMsg)}
                                </CL.Link>
                              </div>
                            )}
                          </CL.GridCol>
                        </CL.GridRow>
                      </CL.Grid>
                    </CL.GridCol>
                  </CL.GridRow>
                ))}
              </CL.Grid>
            </CL.GridCol>
          </CL.GridRow>
        )}
      </CL.Grid>
      {isLastItemModalVisible && (
        <DialogWrapper
          buttons={[
            {
              color: 'link',
              onClick: () => setIsLastItemModalVisible(false),
              text: t.B2V1(cancelMsg),
            },
            {
              onClick: () => removeLastCartItem(),
              text: t.FV8F('Remove item and return to shop'),
            },
          ]}
          closeable
          header={t.F97B('Are you sure you want to remove the last item from your cart?')}
          onCloseDialog={() => setIsLastItemModalVisible(false)}
        />
      )}
    </div>
  );
};
